import * as THREE from "three";
import type { EquipmentInstance, EquipmentSlot } from "../items/EquipmentTypes";
import { RARITY_COLOR } from "../items/Rarity";
import { definitionById } from "../items/Catalog";
import { skinTexture } from "../enemies/CreatureSkins";

/**
 * 10.9GV — VISUAL POR TIER. Antes T1 e T14 eram a mesma placa cinza (só a raridade mudava o brilho).
 * Agora cada faixa de tier tem cor de placa, cor de energia e brilho próprios, e a partir do T5
 * entram adornos (espinhos maiores, anéis flutuantes): dá pra ler o poder da armadura de longe.
 */
const TIER_LOOK: { min: number; plate: number; accent: number; glow: number }[] = [
  { min: 1, plate: 0x34354d, accent: 0xa59aff, glow: 0.9 },
  { min: 4, plate: 0x4a4f7a, accent: 0x9d7bff, glow: 1.0 },
  { min: 7, plate: 0x1c1226, accent: 0xff9a3c, glow: 1.3 }, // Eclipse (Nv 150)
  { min: 8, plate: 0x2a1c4c, accent: 0xc86bff, glow: 1.4 }, // Umbral / Astral
  { min: 10, plate: 0x1b2a4a, accent: 0x62e8ff, glow: 1.5 }, // Sideral / Estelar
  { min: 12, plate: 0x2c1030, accent: 0xff4d6d, glow: 1.6 }, // Nebular / Abissal
  { min: 14, plate: 0x0e0e16, accent: 0xffffff, glow: 1.9 }, // Ômega
];
function tierLook(tier: number): { plate: number; accent: number; glow: number } {
  let out = TIER_LOOK[0];
  for (const l of TIER_LOOK) if (tier >= l.min) out = l;
  return out;
}

export function disposeEquipment(root: THREE.Object3D): void {
  const materials = new Set<THREE.Material>();
  root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    o.geometry.dispose();
    (Array.isArray(o.material) ? o.material : [o.material]).forEach((m) => materials.add(m));
  });
  materials.forEach((m) => m.dispose());
  root.removeFromParent();
}

/** Peças sobre o Hunter original; acompanham os pivôs do corpo e dos membros. */
export function buildArmorPiece(item: EquipmentInstance): THREE.Group {
  const root = new THREE.Group();
  root.name = `equipment_${item.slot}_${item.definitionId}`;
  const id = item.definitionId.toLowerCase();
  const storm = /tempest|gale|storm/.test(id);
  const shard = /shard|warden|colossus|aether/.test(id);
  const tier = (definitionById(item.definitionId) as { tier?: number } | undefined)?.tier ?? 1; // 10.9GV
  const look = tierLook(tier);
  const accent = tier >= 4 ? look.accent : storm ? 0x8ce8ff : shard ? 0xb9a0ff : 0xa59aff;
  const rarityGlow = { common: 0.28, uncommon: 0.45, rare: 0.68, epic: 0.92, legendary: 1.22, mythic: 1.5 }[item.rarity] * look.glow;
  const plateColor = tier >= 4 ? look.plate : storm ? 0x3b6d80 : shard ? 0x625a80 : 0x34354d;
  const plate = new THREE.MeshStandardMaterial({ color: plateColor, metalness: 0.76, roughness: 0.31, flatShading: true, bumpMap: skinTexture("scales", 4), bumpScale: 0.012 }); // 10.9GV: relevo de escamas de metal
  const underlay = new THREE.MeshStandardMaterial({ color: 0x171827, metalness: 0.28, roughness: 0.7, flatShading: true });
  const trim = new THREE.MeshStandardMaterial({ color: RARITY_COLOR[item.rarity], emissive: RARITY_COLOR[item.rarity], emissiveIntensity: rarityGlow * 0.18, metalness: 0.82, roughness: 0.24 });
  const glow = new THREE.MeshStandardMaterial({ color: accent, emissive: accent, emissiveIntensity: rarityGlow, roughness: 0.26, metalness: 0.42 });
  const detail = [...id].reduce((n, c) => n + c.charCodeAt(0), 0) % 3;
  const family = (/^(eclipse|umbral|astral|sideral|stellar|nebular|abyssal|omega)/.exec(id) ?? [])[1] ?? ""; // 10.9GZ
  const add = (g: THREE.BufferGeometry, m: THREE.Material, x: number, y: number, z: number): THREE.Mesh => {
    const mesh = new THREE.Mesh(g, m); mesh.position.set(x, y, z); mesh.castShadow = true; root.add(mesh); return mesh;
  };
  if (item.slot === "head") {
    // Elmo em três camadas: casco, viseira e uma coroa/antena da raridade.
    add(new THREE.SphereGeometry(0.295, 10, 7), underlay, 0, 0.05, -0.015).scale.set(1, 0.85, 0.98);
    add(new THREE.CylinderGeometry(0.285, 0.31, 0.29, 9), plate, 0, 0.08, 0.015);
    add(new THREE.BoxGeometry(0.39, 0.075, 0.085), glow, 0, 0.035, 0.294);
    add(new THREE.BoxGeometry(0.31, 0.04, 0.105), trim, 0, 0.12, 0.3);
    for (const side of [-1, 1]) {
      const crest = add(new THREE.ConeGeometry(0.08, 0.22 + detail * 0.06 + (tier >= 5 ? 0.1 : 0), 5), trim, side * 0.22, 0.27, -0.04);
      crest.rotation.z = side * -0.3;
    }
    // 10.9GZ — assinatura da FAMÍLIA no elmo
    if (family === "eclipse") for (const side of [-1, 1]) { const horn = add(new THREE.ConeGeometry(0.05, 0.34, 5), glow, side * 0.24, 0.3, -0.02); horn.rotation.z = side * -0.75; }
    else if (family === "omega") { const crown = add(new THREE.TorusGeometry(0.2, 0.03, 6, 12), glow, 0, 0.38, 0); crown.rotation.x = Math.PI / 2; for (let i = 0; i < 4; i++) { const a = (i / 4) * Math.PI * 2; add(new THREE.ConeGeometry(0.035, 0.14, 4), glow, Math.cos(a) * 0.2, 0.46, Math.sin(a) * 0.2); } }
    else if (family === "sideral" || family === "stellar") { const crest = add(new THREE.OctahedronGeometry(0.06, 0), glow, 0, 0.42, -0.04); crest.scale.set(0.7, 3.2, 0.7); }
    else if (family === "abyssal" || family === "nebular") for (let i = -1; i <= 1; i++) { const sp = add(new THREE.ConeGeometry(0.035, 0.2, 4), glow, i * 0.14, 0.36, -0.06); sp.rotation.z = i * -0.4; }
    else if (family === "umbral" || family === "astral") for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; add(new THREE.TetrahedronGeometry(0.045, 0), glow, Math.cos(a) * 0.3, 0.3 + (i % 2) * 0.1, Math.sin(a) * 0.3); }
    // 10.9GV — T8+: aro de energia flutuando sobre o elmo (T12+: maior e mais aceso)
    if (tier >= 8) {
      const halo = add(new THREE.TorusGeometry(tier >= 12 ? 0.3 : 0.24, 0.022, 6, 22), glow, 0, 0.44, -0.02);
      halo.rotation.x = Math.PI / 2 + 0.25;
    }
  } else if (item.slot === "chest") {
    // Peitoral segmentado: base flexível, placas frontais e núcleo visível.
    add(new THREE.CylinderGeometry(0.345, 0.39, 0.64, 9), underlay, 0, 0, 0);
    add(new THREE.CylinderGeometry(0.325, 0.37, 0.57, 9), plate, 0, 0.02, 0.045);
    for (const y of [-0.16, 0.04, 0.23]) {
      const panel = add(new THREE.BoxGeometry(0.43 - Math.abs(y) * 0.22, 0.14, 0.075), plate, 0, y, 0.35);
      panel.rotation.x = -0.08;
    }
    add(new THREE.OctahedronGeometry(0.14 + detail * 0.02), glow, 0, 0.08, 0.4);
    add(new THREE.TorusGeometry(0.18, 0.023, 6, 10), trim, 0, 0.08, 0.39).rotation.x = Math.PI / 2;
    for (const side of [-1, 1]) {
      const shoulder = add(new THREE.SphereGeometry(0.23, 8, 6), plate, side * 0.44, 0.24, 0);
      shoulder.scale.set(1.15, 0.65, 1.1);
      add(new THREE.ConeGeometry(0.075, 0.24 + detail * 0.05, 5), trim, side * 0.45, 0.42, -0.05);
      // 10.9GV — T5+: espinho extra de energia no ombro, apontando pra fora
      if (tier >= 5) { const sp = add(new THREE.ConeGeometry(0.06, 0.3, 4), glow, side * 0.58, 0.34, 0); sp.rotation.z = side * -0.9; }
    }
    // 10.9GZ — assinatura da FAMÍLIA no peito
    if (family === "omega") for (const side of [-1, 1]) { const wing = add(new THREE.BoxGeometry(0.55, 0.07, 0.03), glow, side * 0.55, 0.42, -0.3); wing.rotation.z = side * 0.5; const w2 = add(new THREE.BoxGeometry(0.4, 0.05, 0.03), glow, side * 0.5, 0.28, -0.3); w2.rotation.z = side * 0.2; }
    else if (family === "eclipse") { const cres = add(new THREE.TorusGeometry(0.22, 0.03, 6, 18, Math.PI), glow, 0, 0.1, 0.42); cres.rotation.z = Math.PI; }
    else if (family === "umbral" || family === "astral") for (let i = 0; i < 3; i++) { const a = (i / 3) * Math.PI * 2; const sh = add(new THREE.OctahedronGeometry(0.06, 0), glow, Math.cos(a) * 0.5, 0.15 + i * 0.12, -0.35 + Math.sin(a) * 0.1); sh.scale.set(0.6, 2.2, 0.6); }
    else if (family === "sideral" || family === "stellar") for (const side of [-1, 1]) { const ice = add(new THREE.OctahedronGeometry(0.07, 0), glow, side * 0.5, 0.5, 0); ice.scale.set(0.6, 2.6, 0.6); ice.rotation.z = side * -0.4; }
    else if (family === "abyssal" || family === "nebular") for (const side of [-1, 1]) for (let i = 0; i < 2; i++) { const sp = add(new THREE.ConeGeometry(0.04, 0.22, 4), glow, side * (0.5 + i * 0.1), 0.42 + i * 0.1, 0); sp.rotation.z = side * -1.1; }
    // 10.9GV — T8+: anel de energia flutuando nas costas (T12+: dois anéis cruzados)
    if (tier >= 8) {
      const r1 = add(new THREE.TorusGeometry(0.42, 0.028, 6, 26), glow, 0, 0.3, -0.34);
      r1.rotation.y = 0.15;
      if (tier >= 12) { const r2 = add(new THREE.TorusGeometry(0.52, 0.022, 6, 26), glow, 0, 0.3, -0.36); r2.rotation.set(0.5, 0, 0.5); }
    }
  } else if (item.slot === "gloves") {
    add(new THREE.CylinderGeometry(0.14, 0.105, 0.29, 7), underlay, 0, 0, 0);
    add(new THREE.CylinderGeometry(0.125, 0.095, 0.25, 7), plate, 0, 0.015, 0.04);
    add(new THREE.SphereGeometry(0.135, 8, 6), trim, 0, -0.15, 0.06).scale.set(1, 0.72, 1.15);
    add(new THREE.OctahedronGeometry(0.06 + detail * 0.01), glow, 0, 0.04, 0.15);
  } else if (item.slot === "boots") {
    add(new THREE.CylinderGeometry(0.145, 0.13, 0.32, 7), underlay, 0, 0, 0);
    add(new THREE.CylinderGeometry(0.13, 0.115, 0.29, 7), plate, 0, 0.02, 0.025);
    const toe = add(new THREE.SphereGeometry(0.145, 8, 6), trim, 0, -0.18, 0.11);
    toe.scale.set(1, 0.65, 1.7);
    add(new THREE.OctahedronGeometry(0.055 + detail * 0.01), glow, 0, 0.07, 0.12);
  } else {
    // Talismã pendente: aro, gema em duas faces e corrente curta.
    add(new THREE.TorusGeometry(0.2, 0.038, 7, 14), trim, 0, 0, 0);
    add(new THREE.OctahedronGeometry(0.15 + detail * 0.025), underlay, 0, 0, 0.01);
    add(new THREE.OctahedronGeometry(0.115 + detail * 0.02), glow, 0, 0, 0.075);
    add(new THREE.CylinderGeometry(0.023, 0.023, 0.29, 5), trim, 0, -0.26, 0);
    add(new THREE.ConeGeometry(0.07, 0.25, 5), plate, 0, -0.5, 0);
  }
  // Materiais não usados pelo slot não precisam permanecer alocados.
  const used = new Set<THREE.Material>();
  root.traverse((o) => { if (o instanceof THREE.Mesh && !Array.isArray(o.material)) used.add(o.material); });
  for (const m of [plate, underlay, trim, glow]) if (!used.has(m)) m.dispose();
  return root;
}

export class HunterEquipment {
  private readonly equipped = new Map<EquipmentSlot, { key: string; meshes: THREE.Group[] }>();
  constructor(private readonly anchors: Partial<Record<EquipmentSlot, THREE.Object3D[]>>) {}

  update(items: readonly (EquipmentInstance | null)[]): void {
    for (const slot of ["head", "chest", "gloves", "boots", "accessory"] as EquipmentSlot[]) {
      const item = items.find((i) => i?.slot === slot);
      const key = item ? `${item.definitionId}:${item.rarity}` : "";
      const old = this.equipped.get(slot);
      if (old?.key === key) continue;
      old?.meshes.forEach(disposeEquipment);
      this.equipped.delete(slot);
      if (!item) continue;
      const meshes = (this.anchors[slot] ?? []).map((anchor) => {
        const piece = buildArmorPiece(item); anchor.add(piece); return piece;
      });
      this.equipped.set(slot, { key, meshes });
    }
  }
}
